/*
 * CAPTURE-OWNED MODULE
 * v1a step 6 — pure decision logic for the document-type correction control on
 * ReviewDetailPage. capture-ui has no component-test harness (no RTL, on
 * purpose — see signupPolicy.js), so every branch a test must pin lives here
 * as plain functions with no I/O; the network calls stay in services/api.js.
 */

// Build the picker's option list from GET /templates/document-types rows.
// That endpoint returns tenant AND global rows with NO is_active filter
// (templates.ts:736) — filtering is the client's job, and offering an inactive
// type would only 400 at PATCH time.
export function selectableDocumentTypes(rows) {
  const byKey = new Map();
  for (const row of rows || []) {
    if (!row || !row.key) continue;
    const existing = byKey.get(row.key);
    // Tenant row wins over a global row for the same key, mirroring the
    // server's resolution rule (the preflight's tenant-row-wins pick).
    if (!existing || (existing.tenant_id == null && row.tenant_id != null)) {
      byKey.set(row.key, row);
    }
  }
  return [...byKey.values()]
    .filter((r) => r.is_active === true)
    // 'unknown' is a non-answer, not a positive human assertion — the server
    // 400s it as a target. It is tolerated as the CURRENT value only.
    .filter((r) => r.key !== 'unknown')
    .sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999))
    .map((r) => ({ key: r.key, label: r.label || r.key }));
}

// D2: the warning wires to retainsAccess === false and NOTHING else.
// departmentWillChange is true on nearly every correction (117/117 live
// documents are department_key_source='derived'), so a warning keyed on it
// would fire constantly and be ignored — the exact failure D2 avoids. It is
// context INSIDE the message, never the trigger.
export function shouldWarnLostAccess(preflight) {
  return !!preflight && preflight.ok === true && preflight.retainsAccess === false;
}

export function lostAccessMessage(preflight, typeLabel, formatKey) {
  const fmt = formatKey || ((k) => k);
  const dest =
    preflight && preflight.departmentWillChange && preflight.targetDepartmentKey
      ? fmt(preflight.targetDepartmentKey)
      : null;
  const move = dest
    ? `moves this document to the ${dest} department, which you do not have access to`
    : 'moves this document outside the departments you have access to';
  return `Correcting to ${typeLabel} ${move}. It will disappear from your view after the correction, and you will not be able to revert it — an admin can.`;
}

// Q9 ruling: confirm stays disabled until the preflight for the CURRENT
// selection has resolved, and while the selection is not an actual change.
// `preflight` is the page's { forType, status: 'loading'|'ready'|'error',
// data } record; a stale forType (superseded selection) also disables.
export function confirmDisabled({ selection, currentType, saving, preflight }) {
  if (saving) return true;
  if (!selection || selection === currentType) return true;
  if (!preflight || preflight.forType !== selection) return true;
  if (preflight.status !== 'ready') return true;
  return false;
}

// Distinct handling per the server contract: 502 classification_write_failed
// (funnel hard error — nothing was written, retry is safe) and 400
// invalid_document_type. `details` is human-readable everywhere it exists.
export function correctionErrorMessage(status, data) {
  const details = data && typeof data.details === 'string' ? data.details : null;
  if (status === 502 && data && data.error === 'classification_write_failed') {
    return `The correction could not be written${details ? ` (${details})` : ''}. The document type was not changed — try again.`;
  }
  if (status === 400) {
    return details || 'That document type is not valid for this workspace.';
  }
  if (status === 403) {
    return details || 'You do not have permission to edit this document.';
  }
  if (status === 404) {
    return 'Document not found — it may have been deleted or moved out of your departments.';
  }
  return details || (data && data.error) || 'Failed to correct the document type.';
}
